import { h, render } from 'preact';
import { useState } from 'preact/hooks';
import htm from 'htm';
import { AnimatedMessage } from '../../components/AnimatedMessage.js';
import { IconPinOutline, IconInfo } from '../../components/Icons.js';
import { Tracking } from '../../utils/tracking.js';
import { stripExpertHeading } from './anonymize.js';

const html = htm.bind(h);

/**
 * The tips API delivers the whole answer in a single chunk right before `stream_end`.
 * To keep the typewriter feel of the main chat, this panel keeps typing for up to
 * this many seconds after the stream ends (the main chat uses the default of 0).
 * Other panels can opt in by passing `typingCatchUpSeconds` to `AnimatedMessage`.
 */
const TIPS_TYPING_CATCH_UP_SECONDS = 2;

/**
 * TipsTranscript renders the local expert tips as an anonymous card: a fixed header, the
 * typewriter-animated Markdown body and a fixed footer note, shown once the text is fully typed.
 * Nothing identifying the expert is shown; the backend progress messages are replaced by
 * a generic loading label because they name the expert.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.data - Tips state: { content, pending, streaming, error, empty }.
 * @param {Object} props.ctx - Context: { t, config, onRetry }.
 */
export function TipsTranscript({ data, ctx }) {
  const t = ctx.t;
  const tips = data || {};
  const isLoading = tips.pending || tips.streaming;
  // Tips stored by older versions may still start with the expert's name heading.
  const content = stripExpertHeading(tips.content || '');
  const message = { content, streaming: !!tips.streaming };
  // The typewriter is mounted for the whole stream, even before any text arrives, so it sees the
  // live stream. Otherwise text and stream_end arriving in one render batch would be shown at once.
  const showMessage = !!content || isLoading;
  const [isTyped, setIsTyped] = useState(false);

  return html`
    <div class="mta-tips-container" role="log" aria-live="polite" aria-relevant="additions" aria-atomic="false">
      <section class="mta-tips-card" aria-labelledby="mta-tips-card-title">
        <header class="mta-tips-card-header">
          <span class="mta-tips-card-icon"><${IconPinOutline} /></span>
          <div>
            <h3 class="mta-tips-card-title" id="mta-tips-card-title">${t('tips_card_title')}</h3>
            <p class="mta-tips-card-subtitle">${t('tips_card_subtitle')}</p>
          </div>
        </header>
        ${showMessage
          ? html`<div class="mta-tips-body"><${AnimatedMessage} message=${message} typingSpeed=${ctx.config?.typingSpeed} organicTyping=${ctx.config?.organicTyping} typingCatchUpSeconds=${TIPS_TYPING_CATCH_UP_SECONDS} onTypedChange=${setIsTyped} /></div>`
          : ''}
        ${tips.empty && !content
          ? html`<p class="mta-tips-empty">${t('tips_empty')}</p>`
          : ''}
        ${isLoading
          ? html`
            <div class="mta-skeleton" role="status" style="margin-top: ${content ? '8px' : '0'}; flex-direction: row; align-items: center; gap: 8px; padding: 4px 0;">
              <div class="mta-skeleton-line" aria-hidden="true" style="width: 28px; height: 8px; margin: 0; flex-shrink: 0;"></div>
              <div style="font-size: 13px; color: var(--mta-text-muted); font-weight: 500; animation: mta-fade-in 0.3s ease;">${t('tips_loading')}</div>
            </div>
          `
          : ''}
        ${content && !isLoading && isTyped
          ? html`
            <footer class="mta-tips-card-footer">
              <span class="mta-tips-card-footer-icon"><${IconInfo} /></span>
              <span>${t('tips_card_footer')}</span>
            </footer>
          `
          : ''}
      </section>

      ${tips.error
        ? html`
          <div class="mta-error-box" role="alert">
            <span class="mta-error-message">${typeof tips.error === 'string' ? tips.error : t('error_connection')}</span>
            ${ctx.onRetry ? html`
              <button type="button" class="mta-retry-btn" onClick=${() => ctx.onRetry()}>
                ${t('retry')}
              </button>
            ` : ''}
          </div>
        `
        : ''}
    </div>
  `;
}

/**
 * mountTipsView instantiates the Preact rendering flow inside the provided container
 * and keeps the view scrolled to the bottom while the answer is being typed
 * (unless the visitor scrolled up), mirroring the main transcript behaviour.
 *
 * @param {HTMLElement} container - Element target to inject the Preact root.
 * @param {Object} data - Tips state payload.
 * @param {Object} ctx - View context: { t, config, label, onRetry, onAction }.
 * @returns {{ destroy: Function, update: Function }}
 */
export function mountTipsView(container, data, ctx) {
  let stickToBottom = true;

  const getScroller = () => container.querySelector('.mta-tips-container');

  const handleClick = (e) => {
    const actionBtn = e.target.closest('[data-mta-action]');
    if (actionBtn && ctx.onAction) {
      e.preventDefault();
      ctx.onAction(actionBtn.dataset.mtaAction);
      return;
    }
    const link = e.target.closest('a');
    if (link) {
      const clientName = ctx.config?.client || '';
      Tracking.trackResultClick(clientName, link.innerText || link.textContent || '', link.href);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;
    if (!el || !el.classList || !el.classList.contains('mta-tips-container')) return;
    stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
  };

  container.addEventListener('click', handleClick);
  // Scroll events do not bubble; capture them on the container instead.
  container.addEventListener('scroll', handleScroll, true);

  const observer = new MutationObserver(() => {
    const el = getScroller();
    if (el && stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  });
  observer.observe(container, { childList: true, subtree: true, characterData: true });

  render(html`<${TipsTranscript} data=${data} ctx=${ctx} />`, container);

  return {
    destroy() {
      observer.disconnect();
      container.removeEventListener('click', handleClick);
      container.removeEventListener('scroll', handleScroll, true);
      render(null, container);
    },
    update(newData) {
      render(html`<${TipsTranscript} data=${newData} ctx=${ctx} />`, container);
    }
  };
}
